import cuid from "cuid";
import yaml from "js-yaml";
import { get, isEmpty, isNil, keyBy } from "lodash";
import {
  EDGE_KINDS,
  NODE_TYPES,
  PROCESS_TRIGGER_NAMES,
  TEMPLATE_TYPES,
} from "utils/constants";
import { humanizeAccessor, normalizeOperation } from "./catalog";

export const parseConfig = (text, filename = "") => {
  const isJson =
    filename.toLowerCase().endsWith(".json") || text.trim().startsWith("{");
  return isJson ? JSON.parse(text) : yaml.load(text);
};

/** Parcours widget kinds → form / FieldDefinition input kinds. */
const FIELD_TYPE_ALIASES = {
  cards: "radioGroup",
  radio: "radioGroup",
  range: "slider",
  tel: "phone",
  dropdown: "select",
  string: "text",
  toggle: "checkbox",
  switch: "checkbox",
};

const toCamel = (value) =>
  String(value).replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());

const normalizeFieldType = (type) => {
  if (isNil(type) || String(type).trim() === "") return null;
  const raw = String(type).trim();
  return FIELD_TYPE_ALIASES[raw] || raw;
};

const normalizeFormDefinition = (formDefinition) => {
  if (!formDefinition) return null;
  return {
    ...formDefinition,
    strict: isNil(formDefinition.strict) ? true : formDefinition.strict,
    fields: (formDefinition.fields || []).map((field) => ({
      ...field,
      // Absent display means input mode (same as FormDefinitionBuilder).
      display: isNil(field.display) ? false : field.display,
      rules: field.rules || [],
      inputConfig: field.inputConfig || {},
      visibleWhen: field.visibleWhen || [],
    })),
  };
};

/**
 * Top-level `fields` / `mappings` / `referentiels` describe the dictionary;
 * per-status `fields` lists which of those belong on the step form.
 *
 * A status entry may be a source id (`date_naissance`), a custom key
 * (`aideCapital`), a canonical accessor (`user.birthDate`), or an object
 * with those plus label / type / options overrides.
 */
const buildFieldResolvers = (config) => {
  const referentiels = get(config, "referentiels", {}) || {};
  const bySource = {};
  const byKey = {};

  for (const mapping of get(config, "mappings", []) || []) {
    const source = get(mapping, "source");
    const canonical = get(mapping, "canonical");
    if (isNil(source) || isNil(canonical)) continue;
    const path = String(canonical);
    // Canonical identity / contact fields are required in the Obsèques parcours.
    const requiredCanonical =
      path === "user.birthDate" ||
      path === "telecom.email" ||
      path === "telecom.phone" ||
      path === "user.gender" ||
      path === "user.lastname" ||
      path === "user.firstname" ||
      path === "contact.socialRegime" ||
      path === "contact.familySituation" ||
      path === "contact.profession" ||
      path === "telecom.zipCode" ||
      path === "telecom.city" ||
      path === "telecom.advanced.birthCity";
    bySource[String(source)] = {
      accessor: path,
      label: humanizeAccessor(path),
      type: /date$/i.test(path.split(".").pop() || "") ? "date" : null,
      options: null,
      required: requiredCanonical,
    };
  }

  for (const field of get(config, "fields", []) || []) {
    const key = get(field, "key");
    if (isNil(key)) continue;
    const source = get(field, "source") || key;
    const referentielName = get(field, "referentiel");
    const options = isNil(referentielName)
      ? get(field, "options", null)
      : referentiels[referentielName] || null;
    const validation = get(field, "validation");
    const requiredFromValidation =
      !isNil(validation) &&
      !String(validation).toUpperCase().startsWith("OPTIONAL");
    const resolved = {
      accessor: `custom.${key}`,
      label: get(field, "label") || humanizeAccessor(String(key)),
      type: normalizeFieldType(get(field, "type")),
      options,
      required: isNil(get(field, "required"))
        ? requiredFromValidation
        : !!get(field, "required"),
    };
    byKey[String(key)] = resolved;
    bySource[String(source)] = resolved;
  }

  return { bySource, byKey };
};

const importedRightOperand = (condition) => {
  const accessor =
    condition.valueVariable ?? get(condition, "rightOperand.accessor");
  if (!isNil(accessor)) return { accessor: String(accessor) };
  const offsetDays =
    condition.offsetDays ?? get(condition, "rightOperand.offsetDays");
  return {
    value: condition.value ?? get(condition, "rightOperand.value"),
    ...(isNil(offsetDays) ? {} : { offsetDays }),
  };
};

const normalizeVisibleWhen = (visibleWhen) => {
  if (!Array.isArray(visibleWhen) || !visibleWhen.length) return [];
  return visibleWhen
    .map((condition) => {
      if (!condition || typeof condition !== "object") return null;
      if (get(condition, "leftOperand.accessor")) {
        return {
          ...condition,
          operation: normalizeOperation(condition.operation),
        };
      }
      const accessor =
        condition.variable || get(condition, "leftOperand.accessor");
      if (isNil(accessor)) return null;
      return {
        leftOperand: { accessor: String(accessor) },
        operation: normalizeOperation(condition.operation),
        rightOperand: importedRightOperand(condition),
      };
    })
    .filter(Boolean);
};

const resolveStatusFieldRef = (entry, resolvers) => {
  if (isNil(entry)) return null;

  if (typeof entry === "string") {
    const ref = entry.trim();
    if (!ref) return null;
    if (resolvers.bySource[ref]) return { ...resolvers.bySource[ref] };
    if (resolvers.byKey[ref]) return { ...resolvers.byKey[ref] };
    if (ref.includes(".")) {
      return {
        accessor: ref,
        label: humanizeAccessor(ref),
        type: null,
        options: null,
      };
    }
    const camel = toCamel(ref);
    if (resolvers.byKey[camel]) return { ...resolvers.byKey[camel] };
    return {
      accessor: `custom.${camel}`,
      label: humanizeAccessor(camel),
      type: null,
      options: null,
    };
  }

  if (typeof entry !== "object") return null;

  const ref =
    get(entry, "source") ||
    get(entry, "key") ||
    get(entry, "accessor") ||
    get(entry, "canonical");
  const base = resolveStatusFieldRef(ref, resolvers);
  if (!base && isNil(get(entry, "accessor"))) return null;

  const accessor = get(entry, "accessor") || get(base, "accessor");
  if (isNil(accessor)) return null;

  return {
    accessor,
    label:
      get(entry, "label") || get(base, "label") || humanizeAccessor(accessor),
    type: normalizeFieldType(get(entry, "type")) || get(base, "type"),
    options: get(entry, "options", get(base, "options")),
    required: isNil(get(entry, "required"))
      ? get(base, "required")
      : get(entry, "required"),
    display: get(entry, "display"),
    rules: get(entry, "rules", get(base, "rules")) || [],
    inputConfig: get(entry, "inputConfig", get(base, "inputConfig")) || {},
    visibleWhen: normalizeVisibleWhen(
      get(entry, "visibleWhen", get(base, "visibleWhen")),
    ),
  };
};

const formDefinitionFromStatusFields = (statusFields, resolvers) => {
  if (!Array.isArray(statusFields) || statusFields.length === 0) return null;
  const fields = [];
  for (const entry of statusFields) {
    const resolved = resolveStatusFieldRef(entry, resolvers);
    if (!resolved) continue;
    fields.push({
      key: resolved.accessor,
      accessor: resolved.accessor,
      label: resolved.label,
      required: isNil(resolved.required) ? false : !!resolved.required,
      display: isNil(resolved.display) ? false : !!resolved.display,
      options: resolved.options || null,
      type: resolved.type || null,
      rules: resolved.rules || [],
      inputConfig: resolved.inputConfig || {},
      visibleWhen: resolved.visibleWhen || [],
    });
  }
  if (!fields.length) return null;
  return { strict: true, fields };
};

const resolveStatusFormDefinition = (status, resolvers) => {
  if (status.formDefinition)
    return normalizeFormDefinition(status.formDefinition);
  return normalizeFormDefinition(
    formDefinitionFromStatusFields(status.fields, resolvers),
  );
};

const resolveStatusId = (status, existingByName, existingByOrder, usedIds) => {
  const byName = status.name && existingByName[status.name.toLowerCase()];
  if (byName && !usedIds.has(byName.id)) return byName.id;
  if (status.id && !usedIds.has(status.id)) return status.id;
  if (!isNil(status.order) && existingByOrder[String(status.order)]) {
    const byOrder = existingByOrder[String(status.order)];
    if (!usedIds.has(byOrder.id)) return byOrder.id;
  }
  return cuid();
};

const freeProcessId = (id, takenIds) =>
  isEmpty(id) || takenIds.includes(id) ? cuid() : id;

export const configToProcess = (
  config,
  { existingProcess, taken: { processIds = [], statusIds = [] } = {} } = {},
) => {
  if (!config || typeof config !== "object")
    throw new Error("Invalid configuration file");

  const statuses = config.statuses || config.processStatuses || [];
  if (!Array.isArray(statuses) || statuses.length === 0)
    throw new Error("Configuration must define at least one status");

  const existingStatuses = get(existingProcess, "processStatuses", []);
  const existingByName = keyBy(existingStatuses, (status) =>
    (status.name || "").toLowerCase(),
  );
  const existingByOrder = keyBy(
    existingStatuses.filter((status) => !isNil(status.order)),
    (status) => String(status.order),
  );
  const usedIds = new Set(statusIds);

  const prepared = statuses.map((status) => {
    const id = resolveStatusId(
      status,
      existingByName,
      existingByOrder,
      usedIds,
    );
    usedIds.add(id);
    return {
      status,
      id,
      key: status.key || status.name,
    };
  });

  const idByKey = prepared.reduce((map, { key, id }) => {
    if (key) map[key] = id;
    return map;
  }, {});
  const yamlIdToResolved = prepared.reduce((map, { status, id }) => {
    if (status.id) map[status.id] = id;
    return map;
  }, {});
  const remapExportedStatusId = (value) =>
    value && yamlIdToResolved[value] ? yamlIdToResolved[value] : value;

  const resolveScreenFrom = (value) => {
    if (!value) return value;
    if (yamlIdToResolved[value]) return yamlIdToResolved[value];
    if (idByKey[value]) return idByKey[value];
    const byName = existingByName[String(value).toLowerCase()];
    return byName ? byName.id : value;
  };

  const orderedStatuses = prepared
    .filter(({ status }) => !isNil(status.order))
    .sort((a, b) => Number(a.status.order) - Number(b.status.order));

  const nextByStatusId = orderedStatuses.reduce((map, current, index) => {
    const next = orderedStatuses[index + 1];
    if (next) map[current.id] = next.id;
    return map;
  }, {});

  const previousByStatusId = orderedStatuses.reduce((map, current, index) => {
    if (index === 0) return map;
    map[current.id] = orderedStatuses[index - 1].id;
    return map;
  }, {});

  const mapCondition = (condition) => ({
    id: cuid(),
    leftOperand: {
      accessor: condition.variable ?? get(condition, "leftOperand.accessor"),
    },
    operation: normalizeOperation(condition.operation),
    rightOperand: importedRightOperand(condition),
  });

  const mapAction = (action) => {
    if (!action || !action.type) return null;
    const args = { ...(action.args || {}) };
    if (!isNil(action.process)) args.process = action.process;
    if (!isNil(action.status)) {
      args.status = remapExportedStatusId(action.status);
    }
    return {
      id: cuid(),
      type: action.type,
      args,
      conditions: (action.conditions || []).map(mapCondition),
    };
  };

  const resolvedIdSet = prepared.reduce((set, { id }) => {
    set.add(id);
    return set;
  }, new Set());

  const resolveTransitionTarget = (to) => {
    if (!to) return undefined;
    return (
      idByKey[to] ||
      yamlIdToResolved[to] ||
      get(existingByName[String(to).toLowerCase()], "id") ||
      (resolvedIdSet.has(to) ? to : undefined)
    );
  };

  const explicitTransitions = (status) =>
    (status.transitions || []).map((transition) => {
      const target = resolveTransitionTarget(transition.to);
      if (!target)
        throw new Error(
          `Transition target "${transition.to}" not found (status "${
            status.name || status.key
          }")`,
        );
      return {
        id: cuid(),
        nextStatus: target,
        order: transition.order ?? 1,
        kind: transition.kind || EDGE_KINDS.CONDITION,
        event: transition.event ?? null,
        delaySeconds: isNil(transition.delaySeconds)
          ? null
          : transition.delaySeconds,
        conditions: (transition.conditions || []).map(mapCondition),
      };
    });

  const hasBackEvent = (transitions) =>
    transitions.some(
      (transition) =>
        transition.kind === EDGE_KINDS.EVENT &&
        transition.event === PROCESS_TRIGGER_NAMES.BACK,
    );

  const withoutBackEvent = (transitions) =>
    transitions.filter(
      (transition) =>
        !(
          transition.kind === EDGE_KINDS.EVENT &&
          transition.event === PROCESS_TRIGGER_NAMES.BACK
        ),
    );

  /**
   * AWAIT_PAYMENT shows a published screen (System Pay). An EVENT/back edge
   * makes CanvasRunner hide that screen and fall back to the empty
   * "AWAIT_PAYMENT + back" UI after import.
   */
  const rejectsImplicitBack = (nodeType) =>
    nodeType === NODE_TYPES.AWAIT_PAYMENT;

  /** Fallback target when navigation history is empty (linear previous step). */
  const appendBackEdge = (transitions, id, nodeType) => {
    if (rejectsImplicitBack(nodeType)) {
      return withoutBackEvent(transitions);
    }
    const previousId = previousByStatusId[id];
    if (!previousId || hasBackEvent(transitions)) return transitions;
    return [
      ...transitions,
      {
        id: cuid(),
        nextStatus: previousId,
        order: 1000,
        kind: EDGE_KINDS.EVENT,
        event: PROCESS_TRIGGER_NAMES.BACK,
        delaySeconds: null,
        conditions: [],
      },
    ];
  };

  const buildPossibleStatuses = (status, id) => {
    const nodeType = status.nodeType || NODE_TYPES.STATUS;
    const transitions = explicitTransitions(status);
    const nextId = nextByStatusId[id];
    if (transitions.length || isNil(nextId)) {
      return appendBackEdge(transitions, id, nodeType);
    }
    return appendBackEdge(
      [
        {
          id: cuid(),
          nextStatus: nextId,
          order: 1,
          kind: EDGE_KINDS.CONDITION,
          event: null,
          delaySeconds: null,
          conditions: [],
        },
      ],
      id,
      nodeType,
    );
  };

  const embeddedScreens = prepared
    .filter(({ status }) => screenHasEmbeddedMarkup(status.screen))
    .map(({ status, id }) => ({
      id: get(status, "screen.id"),
      statusId: id,
      name: get(status, "screen.name") || status.name,
      htmlContent:
        get(status, "screen.htmlContent") ||
        firstStateMarkup(get(status, "screen.states")) ||
        "",
      styleContent: get(status, "screen.styleContent") || "",
      slots: get(status, "screen.slots") || [],
      translations: get(status, "screen.translations") || {},
      states: get(status, "screen.states") || [],
    }));

  const fieldResolvers = buildFieldResolvers(config);

  const processStatuses = prepared.map(({ status, id }, index) => ({
    id,
    name: status.name,
    tag: status.tag,
    progress: status.progress ?? "",
    color: status.color,
    background: status.background,
    order: String(status.order ?? index + 1),
    template: status.template,
    binding: get(status, "binding") ?? null,
    moveToNext: !!status.moveToNext,
    nodeType: status.nodeType || NODE_TYPES.STATUS,
    formDefinition: resolveStatusFormDefinition(status, fieldResolvers),
    entryConnectors: status.entryConnectors || [],
    outputConnectors: status.outputConnectors || [],
    dataBindings: status.dataBindings || [],
    position: status.position || { x: 0, y: 0 },
    possibleStatuses: buildPossibleStatuses(status, id),
    actions: (status.actions || []).map(mapAction).filter(Boolean),
    config: {
      ...(status.config || {}),
      ...(get(status, "config.screenFrom")
        ? {
            screenFrom: resolveScreenFrom(status.config.screenFrom),
          }
        : {}),
    },
  }));

  const processInput = {
    id: existingProcess
      ? existingProcess.id
      : freeProcessId(config.id, processIds),
    name: config.name ?? get(existingProcess, "name"),
    type: config.type ?? get(existingProcess, "type") ?? "B2B",
    theme:
      config.theme ??
      get(existingProcess, "theme") ??
      TEMPLATE_TYPES.B2B_DYNAMIC,
    description:
      config.description ?? get(existingProcess, "description") ?? "",
    processStatuses,
  };

  if (!processInput.name)
    throw new Error("Configuration must define a process name");

  return { processInput, embeddedScreens };
};

const hasMarkup = (value) => !isNil(value) && String(value).trim() !== "";

const firstStateMarkup = (states = []) => {
  const state = (states || []).find((item) => hasMarkup(item.htmlContent));
  return state ? state.htmlContent : undefined;
};

const versionHasMarkup = (version) => {
  if (!version) return false;
  if (hasMarkup(version.htmlContent)) return true;
  return (version.states || []).some((state) => hasMarkup(state.htmlContent));
};

const screenHasEmbeddedMarkup = (screen) => {
  if (!screen) return false;
  if (hasMarkup(screen.htmlContent)) return true;
  return (screen.states || []).some((state) => hasMarkup(state.htmlContent));
};

const pickExportableVersion = (screen) => {
  const published = get(screen, "currentPublishedVersion");
  if (versionHasMarkup(published)) return published;
  const versions = [...(get(screen, "versions") || [])].sort(
    (left, right) =>
      Number(get(right, "versionNumber") || 0) -
      Number(get(left, "versionNumber") || 0),
  );
  return versions.find(versionHasMarkup) || published || versions[0];
};

const pickExportableScreen = (screens = []) =>
  screens.find((screen) => versionHasMarkup(pickExportableVersion(screen))) ||
  screens[0];

export const attachStatusScreens = (
  graphStatuses = [],
  sourceStatuses = [],
) => {
  const byId = keyBy(sourceStatuses, "id");
  return graphStatuses.map((status) => ({
    ...status,
    screens: get(byId[status.id], "screens") || status.screens || [],
  }));
};

export const attachGraphScreens = (statuses = [], nodes = []) => {
  const byId = keyBy(nodes, "id");
  return statuses.map((status) => {
    if (
      versionHasMarkup(
        pickExportableVersion(pickExportableScreen(status.screens || [])),
      )
    ) {
      return status;
    }
    const data = get(byId[status.id], "data") || {};
    const states = data.screenStates || [];
    if (
      !hasMarkup(data.screenHtml) &&
      !states.some((state) => hasMarkup(state.htmlContent))
    ) {
      return status;
    }
    return {
      ...status,
      screens: [
        {
          currentPublishedVersion: {
            htmlContent: data.screenHtml || "",
            styleContent: data.screenStyleContent || "",
            bindingManifest: { slots: data.screenSlots || [] },
            states,
          },
        },
      ],
    };
  });
};

const pruneEmpty = (object) => {
  const out = {};
  for (const [key, value] of Object.entries(object)) {
    if (isNil(value)) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    )
      continue;
    out[key] = value;
  }
  return out;
};

const exportedCondition = (condition) =>
  pruneEmpty({
    variable: get(condition, "leftOperand.accessor"),
    operation: condition.operation,
    value: get(condition, "rightOperand.value"),
    valueVariable: get(condition, "rightOperand.accessor"),
    offsetDays: get(condition, "rightOperand.offsetDays"),
  });

const screenToConfig = (screen) => {
  const version = pickExportableVersion(screen);
  if (!versionHasMarkup(version)) return undefined;
  return pruneEmpty({
    id: screen.id || undefined,
    name: get(screen, "description") || undefined,
    htmlContent:
      version.htmlContent || firstStateMarkup(version.states) || undefined,
    styleContent: version.styleContent || undefined,
    slots: get(version, "bindingManifest.slots") || undefined,
    translations: get(version, "translationTable.entries") || undefined,
    states: (version.states || []).map((state) =>
      pruneEmpty({
        name: state.name,
        order: isNil(state.order) ? undefined : state.order,
        htmlContent: state.htmlContent || undefined,
        conditions: state.conditions || undefined,
        slots: get(state, "bindingManifest.slots") || state.slots || undefined,
      }),
    ),
  });
};

export const processToConfig = (process) => {
  const statuses = [...(process.processStatuses || [])].sort(
    (a, b) => Number(a.order ?? 0) - Number(b.order ?? 0),
  );
  const nameById = statuses.reduce((map, status) => {
    if (status.id) map[status.id] = status.name;
    return map;
  }, {});

  const mapTransition = (possibleStatus) =>
    pruneEmpty({
      to: nameById[possibleStatus.nextStatus] || possibleStatus.nextStatus,
      kind: possibleStatus.kind || undefined,
      event: possibleStatus.event || undefined,
      delaySeconds: isNil(possibleStatus.delaySeconds)
        ? undefined
        : possibleStatus.delaySeconds,
      order: possibleStatus.order,
      conditions: (possibleStatus.conditions || []).map(exportedCondition),
    });

  const mapAction = (action) =>
    pruneEmpty({
      type: action.type,
      args: action.args || undefined,
      conditions: (action.conditions || []).map(exportedCondition),
    });

  return pruneEmpty({
    id: process.id || undefined,
    name: process.name,
    type: process.type,
    theme: process.theme,
    description: process.description || undefined,
    statuses: statuses.map((status) =>
      pruneEmpty({
        id: status.id || undefined,
        key: status.name,
        name: status.name,
        order: isNil(status.order) ? undefined : Number(status.order),
        tag: status.tag || undefined,
        progress: status.progress || undefined,
        template: status.template || undefined,
        color: status.color || undefined,
        background: status.background || undefined,
        moveToNext: status.moveToNext || undefined,
        binding: status.binding || undefined,
        nodeType: status.nodeType || undefined,
        config: status.config
          ? {
              ...status.config,
              ...(status.config.screenFrom
                ? {
                    screenFrom:
                      nameById[status.config.screenFrom] ||
                      status.config.screenFrom,
                  }
                : {}),
            }
          : undefined,
        formDefinition: status.formDefinition || undefined,
        entryConnectors: status.entryConnectors || undefined,
        outputConnectors: status.outputConnectors || undefined,
        dataBindings: status.dataBindings || undefined,
        position: status.position || undefined,
        actions: (status.actions || []).map(mapAction),
        screen: screenToConfig(pickExportableScreen(status.screens || [])),
        transitions: (status.possibleStatuses || []).map(mapTransition),
      }),
    ),
  });
};

export const serializeConfig = (config, format = "yaml") =>
  format === "json"
    ? JSON.stringify(config, null, 2)
    : yaml.dump(config, { noRefs: true, sortKeys: false, lineWidth: -1 });
